import React, { useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Icon, IconName, Spinner, Tooltip } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { CompanyCard } from 'components/ComponentV2/CompanyCard/CompanyCard.component';
import { ActionMenuButton } from 'components/ActionMenuButton/ActionMenuButton.component';
import { Slider } from 'components/ComponentV2/Slider/Slider.component';
import { useStudentSocieties } from 'hooks/useStudentSocieties';
import { useSponsorshipInvite } from 'hooks/useSponsorshipInvite';
import { usePartnerships } from 'hooks/usePartnerships';
import PendingSponsorshipRequest from 'pages/recruiter/dashboard/PendingSponsorshipRequest';
import { UniversityAttributes } from 'lib/models/university';
import { parseResponse } from 'lib/utils/parser';
import { PER_PAGE_COUNT } from 'lib/models/student-society';
import { PAGE_ROUTES } from 'lib/page-routes';
import { SponsorshipKindEnum } from 'lib/models/sponsor-companies';
import { RemovePartnerModal } from '../RemovePartnerModal/RemovePartnerModal.component';

import styles from './Partnerships.module.scss';

interface PartnershipsProps {
  goToExploreCommunities: () => void;
  onMessageCommunity: (data: { id: string; name: string }) => void;
}

export const Partnerships: React.FC<PartnershipsProps> = (props) => {
  const { goToExploreCommunities, onMessageCommunity } = props;
  const router = useRouter();
  const { societies } = useStudentSocieties();
  const { companySponsorInviteList } = useSponsorshipInvite();
  const {
    isPartnersDataLoading,
    partnerSocietes,
    inConversationPartners,
    removePartner,
  } = usePartnerships();

  const [removeModalData, setRemoveModalData] = useState<
    { name: string; id: string } | undefined
  >(undefined);
  const [partnersTranslate, setPartnersTranslate] = useState<number>(0);
  const [inConvPartnersTranslate, setInConvPartnersTranslate] =
    useState<number>(0);

  const emptyPartnerSteps = useMemo(() => {
    return [
      // {
      //     id: 1,
      //     icon: 'icon_send-message',
      //     title: <><b>Filter & reach out</b> to thousands of community members</>
      // },
      {
        id: 2,
        icon: 'icon_comment',
        title: (
          <>
            <b>Join discussions</b> with students and boost engagement
          </>
        ),
      },
      {
        id: 3,
        icon: 'icon_star-outline',
        title: (
          <>
            <b>Boost discovery</b> by getting featured on society and event
            pages
          </>
        ),
      },
    ];
  }, []);

  const navigateToDashboard = () => {
    router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
  };

  const navigateToSocietyPage = (slug: string) => () => {
    router.push(`${PAGE_ROUTES.STUDENT_SOCIETIES}/${slug}`);
  };

  const getPartnersMenuOptions = ({
    id,
    name,
    slug,
    showDiscussions,
  }: {
    id: string;
    name: string;
    slug: string;
    showDiscussions: boolean;
  }) => {
    const discussionsURL = PAGE_ROUTES.RECRUITER_DISCUSSIONS_PAGE.replace(
      '[id]',
      id
    );
    const options = [
      {
        id: '1',
        label: (
          <p className={styles.menuOption}>
            View profile
            <Icon iconName="icon_launch" size="xsmall" />
          </p>
        ),
        onClick: () =>
          window.open(`${PAGE_ROUTES.SOCIETIES}/${slug}`, '_blank'),
      },
      {
        id: '3',
        label: 'Message',
        onClick: () => onMessageCommunity({ id, name }),
      },
    ];

    if (showDiscussions) {
      options.splice(1, 0, {
        id: '2',
        label: 'View discussions',
        onClick: () => router.push(discussionsURL),
      });
    }

    return options;
  };

  const getInConversationMenuOptions = ({
    id,
    name,
    kind,
    slug,
    inviteId,
  }: {
    id: string;
    name: string;
    kind: SponsorshipKindEnum;
    slug: string;
    inviteId: string;
  }) => {
    const options = [
      {
        id: '2',
        label: 'Message',
        onClick: () => onMessageCommunity({ id, name }),
      },
      {
        id: '3',
        label:
          kind === SponsorshipKindEnum.conversation ? 'Remove' : 'Cancel offer',
        danger: true,
        onClick: () => onRemovePartner(name, inviteId, kind),
      },
    ];

    if (kind === SponsorshipKindEnum.request) {
      options.unshift({
        id: '1',
        label: 'Change offer',
        onClick: () =>
          router.push(`${PAGE_ROUTES.STUDENT_SOCIETIES}/${slug}/detail`),
      });
    }

    return options;
  };

  const onRemovePartner = (
    name: string,
    inviteId: string,
    kind: SponsorshipKindEnum
  ) => {
    if (kind === SponsorshipKindEnum.request) {
      removePartner(inviteId);
    } else if (kind === SponsorshipKindEnum.conversation) {
      setRemoveModalData({ name, id: inviteId });
    }
  };

  const onNavigatePartners = (translateX: number) => {
    setPartnersTranslate(translateX);
  };

  const onNavigateInConvPartners = (translateX: number) => {
    setInConvPartnersTranslate(translateX);
  };

  const renderEmptyState = useMemo(
    () => (
      <div className={styles.PartnershipsContentEmpty}>
        <div>
          {emptyPartnerSteps.map((step) => (
            <div key={step.id} className={styles.emptyStep}>
              <Icon iconName={step.icon as IconName} size="large" />
              <p className={styles.stepTitle}>{step.title}</p>
            </div>
          ))}
        </div>
        <div>
          <ButtonV3
            size="large"
            startIcon="icon_compass"
            onClick={goToExploreCommunities}
          >
            Explore communities
          </ButtonV3>
        </div>
      </div>
    ),
    []
  );

  const renderCommunities = useMemo(
    () => (
      <div className={styles.PartnershipsContentCommunity}>
        <div className={styles.communityTitle}>
          <p>Communities for you</p>
          <ButtonV3
            size="small"
            variant="text"
            className={styles.viewAllAction}
            endIcon="icon_arrow-right-2-bold"
            onClick={goToExploreCommunities}
          >
            View all
          </ButtonV3>
        </div>
        <Slider scrollWidth={270}>
          {societies.slice(0, PER_PAGE_COUNT).map((society) => {
            const {
              id,
              icon_url,
              slug,
              name,
              huzzle_verified,
              is_verified,
              university: universityResponse,
            } = society;
            const university =
              parseResponse<UniversityAttributes>(universityResponse);
            return (
              <CompanyCard
                key={id}
                icon={icon_url}
                name={name}
                title={name}
                caption={university?.name}
                isHuzzleVerified={huzzle_verified}
                isVerified={is_verified}
                onSelect={navigateToSocietyPage(slug)}
              >
                <ButtonV3
                  size="small"
                  color="secondary"
                  onClick={navigateToSocietyPage(slug)}
                >
                  View
                </ButtonV3>
              </CompanyCard>
            );
          })}
        </Slider>
      </div>
    ),
    [societies]
  );

  const renderPartners = useMemo(
    () => (
      <div className={styles.partnerCommunities}>
        <Slider scrollWidth={270} onNavigate={onNavigatePartners}>
          {partnerSocietes.map((society) => {
            const societyData = parseResponse(society.student_society);
            const { id, icon_url, slug, name, huzzle_verified, is_verified } =
              societyData;
            const sponsorshipPlan = parseResponse(society.sponsorship_plan);
            const huzzleFeatures = sponsorshipPlan.huzzle_features.filter(
              (feature) => feature
            );
            // TODO: change this once backend starts sending a boolean value.
            const showDiscussions = huzzleFeatures.includes('Post to forum');

            return (
              <CompanyCard
                key={id}
                icon={icon_url}
                name={name}
                title={name}
                caption=""
                isHuzzleVerified={huzzle_verified}
                isVerified={is_verified}
                onSelect={navigateToSocietyPage(slug)}
                className={styles.dataCard}
              >
                <Tooltip
                  isV2Design
                  color="dark"
                  position="top"
                  content={`Your enabled features include: ${huzzleFeatures.join(
                    ', '
                  )}`}
                  containerClassName={styles.partnerTooltip}
                  className={styles.partnerTooltipContent}
                  style={{ transform: `translateX(-${partnersTranslate}px)` }}
                >
                  <ActionMenuButton
                    className={styles.sliderAction}
                    options={getPartnersMenuOptions({
                      id: society.student_society_id,
                      name,
                      slug,
                      showDiscussions,
                    })}
                    buttonText={`${huzzleFeatures.length} features`}
                    buttonColor="secondary"
                    buttonSize="small"
                    buttonEndIcon="icon_arrow-down"
                    actionMenuStyle={{
                      transform: `translateX(-${partnersTranslate}px)`,
                      marginLeft: '35px',
                    }}
                    menuOpenClassName={styles.menuOpenButton}
                  />
                </Tooltip>
              </CompanyCard>
            );
          })}
        </Slider>
      </div>
    ),
    [partnerSocietes, partnersTranslate]
  );

  const renderInConversationPartners = useMemo(
    () => (
      <div className={styles.partnerCommunities}>
        <p className={styles.title}>In conversation with</p>
        <Slider scrollWidth={270} onNavigate={onNavigateInConvPartners}>
          {inConversationPartners.map((society) => {
            const {
              id: inviteId,
              kind = SponsorshipKindEnum.conversation,
              student_society,
            } = society;
            const societyData = parseResponse(student_society);
            const { id, icon_url, slug, name, huzzle_verified, is_verified } =
              societyData;
            const buttonText =
              kind === SponsorshipKindEnum.request ? 'Offer sent' : 'Continue';

            return (
              <CompanyCard
                key={id}
                icon={icon_url}
                name={name}
                title={name}
                caption=""
                isHuzzleVerified={huzzle_verified}
                isVerified={is_verified}
                onSelect={navigateToSocietyPage(slug)}
                className={styles.dataCard}
              >
                <ActionMenuButton
                  className={styles.sliderAction}
                  options={getInConversationMenuOptions({
                    id,
                    name,
                    kind,
                    slug,
                    inviteId,
                  })}
                  buttonText={buttonText}
                  buttonColor="secondary"
                  buttonSize="small"
                  dropdownDirection="top"
                  buttonEndIcon={
                    kind === SponsorshipKindEnum.request
                      ? 'icon_arrow-down'
                      : undefined
                  }
                  actionMenuStyle={{
                    transform: `translateX(-${inConvPartnersTranslate}px)`,
                    marginLeft: '60px',
                  }}
                  menuOpenClassName={styles.menuOpenButton}
                />
              </CompanyCard>
            );
          })}
        </Slider>
      </div>
    ),
    [inConversationPartners, inConvPartnersTranslate]
  );

  const renderNotices = useMemo(
    () =>
      companySponsorInviteList.map((invite, index) => (
        <PendingSponsorshipRequest key={index} invite={invite} />
      )),
    [companySponsorInviteList]
  );

  return (
    <>
      <div className={styles.Partnerships}>
        <div className={styles.PartnershipsHeader}>
          <div className={styles.breadcrumb} onClick={navigateToDashboard}>
            <Icon size="large" iconName="chevron-left" />
            <p>Dashboard</p>
          </div>
          <p className={styles.pageTitle}>Partnerships</p>
        </div>
        <div className={styles.PartnershipsContent}>
          {isPartnersDataLoading ? (
            <Spinner showBackDrop wrapper size="small" />
          ) : (
            <>
              {companySponsorInviteList.length > 0 && (
                <div className={styles.PartnershipsContentNotices}>
                  {renderNotices}
                </div>
              )}
              {partnerSocietes.length === 0 &&
                inConversationPartners.length === 0 &&
                renderEmptyState}
              {partnerSocietes.length > 0 && renderPartners}
              {inConversationPartners.length > 0 &&
                renderInConversationPartners}
              {societies && societies.length > 0 && renderCommunities}
            </>
          )}
        </div>
      </div>
      <RemovePartnerModal
        open={!!removeModalData}
        onClose={() => setRemoveModalData(undefined)}
        invitationId={removeModalData?.id || ''}
        societyName={removeModalData?.name || ''}
      />
    </>
  );
};
